import React from "react";
import { Link } from "react-router-dom";
import { 
  Users, 
  Sparkles, 
  Zap, 
  Shield, 
  ArrowRight, 
  Play, 
  Crown, 
  DollarSign, 
  Layers, 
  Share2,
  Moon,
  Sun
} from "lucide-react";
import Button from "../components/ui/Button";

const LandingPage = () => {
  const [theme, setTheme] = React.useState("dark");
  
  // Detect if we're on the public domain
  const isPublicDomain = window.location.hostname === 'adnaki.et' || window.location.hostname === 'www.adnaki.et';

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };
  return (
    <div className={`min-h-screen overflow-x-hidden selection:bg-primary/30 ${theme === "dark" ? "text-white" : "text-slate-900 bg-slate-50"}`}>
      {/* Background Image Layer */}
      <div 
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-40 mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: `url(/landing-bg.png)` }}
      />
      <div className={`fixed inset-0 z-0 ${theme === "dark" ? "bg-slate-900/90" : "bg-white/80"} pointer-events-none`} />
      
      {/* Mesh Gradients (Theme Aware) */}
      <div className={`fixed inset-0 z-0 pointer-events-none ${theme === "dark" ? "mesh-bg" : "bg-gradient-to-br from-indigo-100 via-white to-purple-100"}`} />

      {/* --- Navigation --- */}
      <nav className={`fixed top-0 left-0 right-0 z-50 py-4 px-6 md:px-12 flex justify-between items-center border-b transition-all ${theme === "dark" ? "glass-surface border-white/5" : "bg-white/70 border-slate-200 backdrop-blur-md"}`}>
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 premium-gradient rounded-xl flex items-center justify-center shadow-lg neon-glow">
            <span className="text-2xl font-black italic tracking-tighter text-white">A</span>
          </div>
          <span className={`text-2xl font-bold tracking-tight ${theme === "dark" ? "text-white" : "text-slate-900"}`}>ADNAKI</span>
        </div>
        <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-slate-300">
          <a href="#fans" className="hover:text-primary transition-colors">Superfans</a>
          <a href="#creators" className="hover:text-primary transition-colors">Creators</a>
          <a href="#ecosystem" className="hover:text-primary transition-colors">Ecosystem</a>
        </div>
        <div className="flex items-center space-x-4">
          <button 
            onClick={toggleTheme}
            className={`p-2 rounded-full transition-colors ${theme === "dark" ? "bg-white/10 hover:bg-white/20 text-white" : "bg-slate-200 hover:bg-slate-300 text-slate-700"}`}
          >
            {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {!isPublicDomain && (
            <>
              <Link to="/login">
                <Button variant="ghost" className={`text-sm font-bold ${theme === "dark" ? "text-white" : "text-slate-700 hover:bg-slate-200"}`}>Login</Button>
              </Link>
              <Link to="/creator-auth">
                <Button className="premium-gradient text-sm font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all text-white">
                  Join as Creator
                </Button>
              </Link>
            </>
          )}
        </div>
      </nav>
      
      {/* Content Wrapper for Z-Index */}
      <div className="relative z-10">

      {/* --- Hero Section --- */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 flex flex-col items-center text-center">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 blur-[120px] rounded-full -z-10"></div>
        
        <div className="inline-flex items-center px-3 py-1 rounded-full glass-surface border border-white/10 text-xs font-semibold text-primary mb-6 animate-float">
          <Sparkles size={14} className="mr-2" />
          <span>The Future of Fan Engagement is Here</span>
        </div>
        
        <h1 className="text-5xl md:text-8xl font-black tracking-tight leading-[0.9] mb-8 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent italic">
          SUPERFAN & CREATOR <br />
          <span className="text-primary italic">ECOSYSTEM. REDEFINED.</span>
        </h1>
        
        <p className="max-w-2xl text-lg md:text-xl text-slate-400 mb-10 leading-relaxed">
          The all-in-one platform where superfans unlock exclusive experiences and creators automate their growth via state-of-the-art monetization tools.
        </p>

        {!isPublicDomain && (
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
            <Link to="/fan">
              <Button className="h-14 px-8 premium-gradient text-lg font-bold rounded-2xl shadow-2xl shadow-primary/40 flex items-center group">
                Explore as Fan
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/creator-auth">
              <Button variant="outline" className="h-14 px-8 glass-card border-white/10 text-lg font-bold rounded-2xl hover:bg-white/5">
                Launch Creator Portal
              </Button>
            </Link>
          </div>
        )}

        {isPublicDomain ? (
          <div className="mt-20 max-w-3xl mx-auto text-center">
            <p className="text-lg text-slate-400 leading-relaxed">
              Empowering creators to monetize their passion while building deeper connections with their most dedicated fans.
            </p>
          </div>
        ) : (
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 opacity-50">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black tracking-tighter">1M+</span>
              <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Fans</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black tracking-tighter">10K+</span>
              <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Creators</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black tracking-tighter">$50M+</span>
              <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Earned</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black tracking-tighter">24/7</span>
              <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Uptime</span>
            </div>
          </div>
        )}
      </section>

      {/* --- Dual Focus Section --- */}
      <section id="fans" className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Fan Card */}
          <div className="glass-card rounded-[32px] p-8 md:p-12 border-primary/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-3xl -z-10 group-hover:bg-primary/20 transition-all"></div>
            <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-8 text-primary shadow-inner">
              <Crown size={32} />
            </div>
            <h2 className="text-4xl font-black tracking-tighter mb-4 italic">FOR SUPERFANS</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Tired of being just a follower? Become a Superfan. Get closer to your favorite creators than ever before.
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Instant Access to Exclusive Content",
                "Direct Messaging with Creators",
                "Early Access to Limited Drops",
                "Dynamic Community Badges"
              ].map((item, i) => (
                <li key={i} className="flex items-center text-sm font-medium text-slate-200">
                  <div className="w-5 h-5 bg-primary/20 rounded-full flex items-center justify-center mr-3 text-primary">
                    <Zap size={10} fill="currentColor" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            {!isPublicDomain && (
              <Button variant="outline" className="w-full border-white/10 hover:border-primary/50 text-sm font-bold h-12 rounded-xl">
                Start Your Fan Journey
              </Button>
            )}
          </div>

          {/* Creator Card */}
          <div id="creators" className="glass-card rounded-[32px] p-8 md:p-12 border-purple-500/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-3xl -z-10 group-hover:bg-purple-500/20 transition-all"></div>
            <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mb-8 text-purple-400 shadow-inner">
              <DollarSign size={32} />
            </div>
            <h2 className="text-4xl font-black tracking-tighter mb-4 italic text-purple-400 uppercase">For Creators</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Monetize your passion with a suite of tools designed to automate your revenue and maximize your output.
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Advanced Subscription Tiers",
                "Automated Ad Campaigns",
                "Dynamic Payout Systems",
                "AI-Powered Content Hub"
              ].map((item, i) => (
                <li key={i} className="flex items-center text-sm font-medium text-slate-200">
                  <div className="w-5 h-5 bg-purple-500/20 rounded-full flex items-center justify-center mr-3 text-purple-400">
                    <Zap size={10} fill="currentColor" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            {!isPublicDomain && (
              <Button variant="outline" className="w-full border-white/10 hover:border-purple-500/50 text-sm font-bold h-12 rounded-xl">
                Join the Elite Creators
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* --- Ecosystem Stats --- */}
      <section id="ecosystem" className="py-24 px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter italic mb-12">ONE UNIFIED ECOSYSTEM</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                {[
                    { title: "Safe & Secure", icon: Shield, desc: "End-to-end encryption for all messages and transactions." },
                    { title: "Seamless UI", icon: Layers, desc: "A world-class experience across mobile and desktop." },
                    { title: "Viral Growth", icon: Share2, desc: "Integrated social sharing and discovery tools." }
                ].map((item, i) => (
                    <div key={i} className="p-6 rounded-3xl glass-surface border border-white/5 hover:border-white/10 transition-all group">
                        <div className="p-3 bg-white/5 rounded-2xl w-fit mb-4 group-hover:text-primary transition-colors">
                            <item.icon size={24} />
                        </div>
                        <h4 className="font-bold text-lg text-white mb-2">{item.title}</h4>
                        <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="py-12 border-t border-white/5 px-6 md:px-12 glass-surface">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 premium-gradient rounded-lg flex items-center justify-center">
              <span className="text-lg font-black italic tracking-tighter">A</span>
            </div>
            <span className="text-xl font-bold tracking-tight text-white">ADNAKI</span>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-12 text-sm font-medium text-slate-500">
            <Link to="/login" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/login" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/login" className="hover:text-white transition-colors">Help Center</Link>
          </div>

          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Copyright by{" "}
            <a 
              href="https://ride8294.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-primary hover:underline font-bold"
            >
              RIDE and its Affiliates
            </a>
          </p>
        </div>
      </footer>
      </div>{/* Close content wrapper */}
    </div>
  );
};

export default LandingPage;
